import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../customer/customers.theme';
import { COPY } from './billing.copy';
import { CloseIcon } from './BillingIcons';
import { notifyOf, shouldPrompt, isLocked, LOOP } from './billing.rules';
import { openPay, hideBanner } from './billingSlice';

/**
 * The strip in the app bar.
 *
 * Deliberately small — a pill in the middle of the bar rather than a band
 * across the screen. A band would have to push every screen down, and the
 * screens below size themselves against the bar's height (`calc(100vh -
 * 90px)`), so a few extra pixels up here would put a scrollbar on every tab
 * in the app. This changes nothing about the layout: it sits in the space
 * that was already empty between the wordmark and the avatar.
 *
 * Two faces. Amber with a ✕ when the bill is merely due; red without one when
 * the till is locked. Red is already the app's danger colour, so nobody has
 * to learn what it means.
 *
 * Renders nothing at all for a waiter, and nothing when nothing is owed.
 */
export default function BillingBanner() {
  const dispatch = useDispatch();
  const activeUser = useSelector((state) => state.login.activeUser);
  const business = useSelector((state) => state.login.business) || activeUser?.business;
  const hidden = useSelector((state) => state.billing.bannerHidden);

  if (!shouldPrompt(activeUser?.role, business)) return null;

  const locked = isLocked(business);

  // The ✕ only silences a reminder. A locked till has no ✕ and this can never
  // be true for it.
  if (hidden && notifyOf(business) === LOOP) return null;

  const tint = locked ? T.dangerTint : T.pendingTint;
  const edge = locked ? T.dangerBorder : T.pendingBorder;
  const ink = locked ? '#9B1C1C' : '#7A4E06';
  const dot = locked ? T.danger : T.pendingInk;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        // The app bar is a flex row with no `alignItems` of its own, so
        // without this the pill would hang from the top of the bar instead of
        // sitting on its centre line.
        alignSelf: 'center',
        gap: 10,
        minWidth: 0,
        // Capped so a narrow window truncates the sentence rather than
        // shoving the avatar off the right-hand edge.
        maxWidth: 560,
        margin: '0 16px',
        height: 30,
        padding: '0 6px 0 12px',
        background: tint,
        border: `1px solid ${edge}`,
        borderRadius: T.radiusPill,
        fontFamily: FONT_STACK,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          flex: 'none',
          borderRadius: T.radiusPill,
          background: dot,
          animation: locked ? 'dalabtireBillPulse 1.8s ease-in-out infinite' : 'none',
        }}
      />

      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
        title={COPY.banner}
      >
        {COPY.banner}
      </span>

      <button
        type="button"
        onClick={() => dispatch(openPay())}
        style={{
          flex: 'none',
          height: 22,
          padding: '0 12px',
          border: 'none',
          borderRadius: T.radiusPill,
          background: T.brand,
          color: T.surface,
          font: 'inherit',
          fontFamily: FONT_STACK,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          cursor: 'pointer',
        }}
      >
        {COPY.bannerAction}
      </button>

      {locked ? null : (
        <button
          type="button"
          aria-label={COPY.hideBanner}
          title={COPY.hideBanner}
          onClick={() => dispatch(hideBanner())}
          style={{
            flex: 'none',
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: T.radiusPill,
            background: 'transparent',
            color: ink,
            opacity: 0.55,
            cursor: 'pointer',
          }}
        >
          <CloseIcon size={13} />
        </button>
      )}
    </div>
  );
}

/** The one keyframe this feature owns, rendered once by the gate. */
export const BillingKeyframes = () => (
  <style>
    {'@keyframes dalabtireBillPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }'}
  </style>
);
